import { logger } from "@/shared/lib/logger";
import type { AxiosInstance } from 'axios';
import { User, UsersApiResponse, UsersQueryRequest } from '../types/users.types';
import { apiRoutes } from '@/shared/api/routes';

export interface GetUsersResponse {
  users: User[];
  totalCount: number;
}

const transformUserData = (user: User): User => {
  // Transform API data to match UI expectations
  return {
    ...user,
    username: user.login,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
    status: user.status as 'ENABLED' | 'DISABLED' | 'LOCKED',
    lastLoginAt: user.last_login_at || '',
    rolesText: user.roles?.map(r => r.name).join(', ') || '',
    companiesText: user.companies?.map(c => c.name).join(', ') || '',
    fundingSources: user.funding_sources?.map(fs => fs.name).join(', ') || '',
  };
};

export interface UserUpdatePayload {
  id: number;
  login?: string | undefined;
  email?: string | null | undefined;
  first_name?: string | undefined;
  last_name?: string | undefined;
  middle_initials?: string | null | undefined;
  phone?: string | null | undefined;
  feedback_notify?: boolean | undefined;
  roles?: { id: number }[] | undefined;
  contact_company?: { id: number } | null | undefined;
  default_run_pool?: { id: number } | null | undefined;
  subcontractor?: number | undefined;
  companies?: { id: number }[] | null | undefined;
  funding_sources?: { id: number }[] | null | undefined;
  client_pool?: { id: number } | null | undefined;
  access_unassigned_no_run_trips?: boolean | undefined;
  login_attempt_count?: number | undefined;
  password?: string | undefined;
  status?: 'ENABLED' | 'DISABLED' | undefined;
}

export interface UserCreatePayload {
  login: string;
  email: string;
  first_name: string;
  last_name: string;
  middle_initials?: string | null;
  phone?: string | null;
  password: string;
  feedback_notify?: boolean;
  roles?: { id: number }[];
  contact_company?: { id: number } | null;
  default_run_pool?: { id: number } | null;
  subcontractor?: number;
  companies?: { id: number }[] | null;
  funding_sources?: { id: number }[];
  client_pool?: { id: number } | null;
  access_unassigned_no_run_trips?: boolean;
}

export interface CheckLoginExistsResponse {
  data: { id: number }[];
  info: {
    limit: number;
    results: number;
    elapsed_total: number;
    db_query: string;
    db_query_parameters: Record<string, string>;
    select_query: boolean;
  };
}

interface UserCreateApiResponse {
  result: string;
  success: boolean;
  failure: boolean;
  api_version: number;
  successes: { resource_id: string }[];
}

export const createUsersApi = (apiClient: AxiosInstance) => ({
  getUsers: async (
    offset: number = 0,
    limit: number = 50,
    orderBy?: [string, string][],
    query: unknown[] = [],
    quicksearch?: string,
  ): Promise<GetUsersResponse> => {
    try {
      const queryRequest: UsersQueryRequest = {
        filter: [
          "access_unassigned_no_run_trips",
          "email",
          "feedback_notify",
          "first_name",
          "id",
          "last_login_at",
          "last_name",
          "login",
          "login_attempt_count",
          "middle_initials",
          "origin",
          "phone",
          "status",
          "subcontractor",
          ["contact_company", "id", "name"],
          ["default_run_pool", "id", "name"],
          ["roles", "id", "name"],
          ["companies", "id", "name"],
          ["funding_sources", "id", "name", "fare_rounding"],
          ["client_pool", "id", "name"]
        ],
        query: [
          ...query,
          ...(quicksearch?.trim() ? [
            ["or",
              ["like", "login", `%${quicksearch.trim()}%`],
              ["like", "first_name", `%${quicksearch.trim()}%`],
              ["like", "last_name", `%${quicksearch.trim()}%`],
              ["like", "email", `%${quicksearch.trim()}%`],
            ]
          ] : []),
        ],
        options: {
          timeout: 10,
          offset,
          limit,
          order: orderBy || [["login", "asc_nulls_last"]],
          calculate_total: true,
          return_results: true,
          select_distinct: true
        }
      };

      const response = await apiClient.post<UsersApiResponse>(apiRoutes.proxy('support/internal/user/query'),
        queryRequest
      );

      // Handle case when there are no results (data array may be undefined/null)
      const users = response.data.data || [];
      const transformedUsers = users.map(transformUserData);

      return {
        users: transformedUsers,
        totalCount: response.data.info?.total_results ?? (response.data as any).total ?? 0,
      };
    } catch (error) {
      logger.error('Failed to fetch users:', error);
      throw new Error('Failed to load users');
    }
  },

  updateUser: async (userId: number, payload: UserUpdatePayload): Promise<User> => {
    try {
      const response = await apiClient.put<User>(
        apiRoutes.proxy(`support/internal/user/${userId}`),
        payload,
        // skip401: prevent the global 401 interceptor from hard-redirecting to /login
        // when the backend invalidates the session after a password change. The caller
        // (useUpdateUser / Navbar) is responsible for re-bootstrapping the session.
        { skip401: true }
      );

      return transformUserData(response.data);
    } catch (error) {
      logger.error('Failed to update user:', error);
      throw error;
    }
  },

  // Sends a password reset email. Routes through the same endpoint as the
  // public forgot-password flow (Api::PasswordsController#request_reset), which
  // both generates the K3 token AND delivers the email via LostPasswordMailer.
  // The previous implementation proxied straight to K3's
  // generate_password_reset_token, which only mints a token and never sends
  // an email — so the admin "Reset password" action silently did nothing.
  resetPassword: async (email: string): Promise<void> => {
    try {
      await apiClient.post(
        `${apiRoutes.forgotPassword()}/passwords/request_reset`,
        {
          email,
          frontend_url: window.location.origin,
        }
      );
    } catch (error) {
      logger.error('Failed to reset password:', error);
      throw error;
    }
  },

  checkLoginExists: async (login: string): Promise<boolean> => {
    try {
      const response = await apiClient.post<CheckLoginExistsResponse>(
        apiRoutes.proxy('support/internal/user/select_query'),
        {
          select: ['id'],
          query: [['equal', 'login', login]],
          options: {
            timeout: 10,
            offset: 0,
            limit: 1,
            calculate_total: false,
            return_results: true,
            select_distinct: true
          }
        }
      );

      // If results > 0, login already exists
      return response.data.data.length > 0;
    } catch (error) {
      logger.error('Failed to check login existence:', error);
      throw error;
    }
  },

  createUser: async (payload: UserCreatePayload): Promise<User> => {
    try {
      const response = await apiClient.post<UserCreateApiResponse>(
        apiRoutes.proxy('support/internal/user'),
        payload
      );

      const resourceId = Number(response.data.successes?.[0]?.resource_id);
      return { id: resourceId } as User;
    } catch (error) {
      logger.error('Failed to create user:', error);
      throw error;
    }
  },

  deleteUser: async (userId: number): Promise<void> => {
    try {
      await apiClient.delete(
        apiRoutes.proxy(`support/internal/user/${userId}`)
      );
    } catch (error) {
      logger.error('Failed to delete user:', error);
      throw error;
    }
  },

  checkPasswordPolicy: async (password: string): Promise<void> => {
    try {
      await apiClient.post(
        apiRoutes.proxy('support/internal/session/password_check'),
        { password }
      );
    } catch (error) {
      logger.error('Password policy check failed:', error);
      throw error;
    }
  },
});

export type UsersApi = ReturnType<typeof createUsersApi>;
